import React from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import { useTrading } from '../context/TradingContext'
import { EmptyState } from '../components/Loader'
import { formatToman, toFaDigits, formatNumberFa } from '../utils/format'
import { TYPE_LABELS, CATEGORY_LABELS, OrderType, MASKED_PRICE } from '../types'

/** یک پنل کامل سبد برای یک سمت معامله */
const CartPanel: React.FC<{ mode: OrderType }> = ({ mode }) => {
  const { itemsOf, updateQuantity, removeItem, subtotalOf, shippingOf, totalOf, unitPriceOf } = useCart()
  const { buyBlocked, sellBlocked, pricesHidden } = useTrading()

  const isBuy = mode === 'BUY'
  const items = itemsOf(mode)
  const subtotal = subtotalOf(mode)
  const shipping = shippingOf(mode)
  const total = totalOf(mode)
  const blocked = isBuy ? buyBlocked : sellBlocked

  const accent = isBuy
    ? { header: 'bg-primary-600', border: 'border-primary-200', text: 'text-primary-700', btn: 'btn-primary' }
    : { header: 'bg-gold-600', border: 'border-gold-300', text: 'text-gold-800', btn: '!bg-gold-600 !text-white hover:!bg-gold-700' }

  return (
    <div className={`card overflow-hidden flex flex-col border-2 ${accent.border}`}>
      <div className={`${accent.header} text-white px-5 py-4 flex items-center justify-between`}>
        <div>
          <h2 className="font-black">{isBuy ? 'سبد خرید شما از ما' : 'سبد فروش شما به ما'}</h2>
          <p className="text-[11px] opacity-90 mt-0.5">
            {isBuy ? 'سفارش‌های خرید — ما فلز به شما می‌فروشیم' : 'سفارش‌های فروش — ما فلز از شما می‌خریم'}
          </p>
        </div>
        <span className="bg-white/20 rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap">
          {toFaDigits(items.reduce((s, i) => s + i.quantity, 0))} قلم
        </span>
      </div>

      {blocked && (
        <div className="bg-red-50 border-b-2 border-red-200 px-5 py-3 text-xs text-red-700 leading-6">
          <b>⛔ این بخش موقتاً بسته است.</b>{' '}
          {isBuy
            ? 'معاملات فروش توسط مدیر متوقف شده است.'
            : 'خرید از مشتریان توسط مدیر متوقف شده است.'}
        </div>
      )}

      <div className="flex-1 divide-y divide-gray-50">
        {items.length === 0 ? (
          <div className="py-14 text-center text-sm text-gray-400">
            {isBuy ? 'سبد خرید شما خالی است' : 'سبد فروش شما خالی است'}
          </div>
        ) : (
          items.map(({ product, quantity }) => {
            const unit = unitPriceOf(product, mode)
            const hidden = pricesHidden || product.priceHidden || unit === 0
            return (
              <div key={`${mode}-${product.id}`} className="p-4 flex flex-wrap sm:flex-nowrap sm:items-center gap-3">
                <div className="flex-1 min-w-[180px] sm:min-w-0 basis-full sm:basis-auto">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`badge ${product.type === 'GOLD' ? 'badge-gold' : 'badge-silver'}`}>
                      {TYPE_LABELS[product.type]}
                    </span>
                    <span className="badge bg-primary-50 text-primary-700">{CATEGORY_LABELS[product.category]}</span>
                  </div>
                  <Link to={`/products/${product.slug}`} className="font-bold text-gray-900 hover:text-primary-600 block truncate">
                    {product.name}
                  </Link>
                  <p className="text-xs text-gray-500 mt-1">
                    قیمت واحد: {hidden ? MASKED_PRICE : formatToman(unit)} • وزن کل: {toFaDigits(product.weight * quantity)} گرم
                  </p>
                </div>

                <div className="flex items-center justify-between gap-3 w-full sm:w-auto sm:ms-auto">
                  <div className="flex items-center gap-1 border border-gray-300 rounded-lg">
                    <button className="px-3 py-1.5 hover:bg-gray-100" onClick={() => updateQuantity(product.id, mode, quantity - 1)}>−</button>
                    <span className="px-3 py-1.5 border-x border-gray-200 font-bold text-sm">{toFaDigits(quantity)}</span>
                    <button className="px-3 py-1.5 hover:bg-gray-100" onClick={() => updateQuantity(product.id, mode, quantity + 1)}>+</button>
                  </div>

                  <div className="text-left">
                    <div className={`font-black ${accent.text} text-sm sm:text-base`}>
                      {hidden ? MASKED_PRICE : formatToman(unit * quantity)}
                    </div>
                    <button className="text-xs text-red-500 hover:underline mt-1" onClick={() => removeItem(product.id, mode)}>
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      <div className="border-t border-gray-100 p-5 bg-gray-50">
        <div className="space-y-2 text-sm mb-4">
          <div className="flex justify-between">
            <span className="text-gray-600">{isBuy ? 'جمع کالاها' : 'جمع دریافتی شما'}</span>
            <span className="font-bold">{pricesHidden ? MASKED_PRICE : formatToman(subtotal)}</span>
          </div>
          {isBuy && (
            <div className="flex justify-between">
              <span className="text-gray-600">هزینه ارسال</span>
              <span className="font-bold">{shipping === 0 ? 'رایگان' : formatToman(shipping)}</span>
            </div>
          )}
          <div className="border-t border-gray-200 pt-2 flex justify-between text-base">
            <span className="font-bold text-gray-900">{isBuy ? 'مبلغ کل' : 'مبلغ کل دریافتی'}</span>
            <span className={`font-black ${accent.text}`}>{pricesHidden ? MASKED_PRICE : formatToman(total)}</span>
          </div>
          {isBuy && shipping > 0 && (
            <p className="text-xs text-gray-400">
              برای ارسال رایگان، {formatNumberFa(Math.max(0, (50000000 - subtotal) / 10))} تومان دیگر خرید کنید.
            </p>
          )}
        </div>

        <Link
          to={`/checkout?mode=${mode}`}
          className={`w-full !py-3 text-center ${
            blocked || items.length === 0
              ? 'btn bg-gray-100 text-gray-400 cursor-not-allowed pointer-events-none'
              : `btn ${accent.btn}`
          }`}
        >
          {blocked
            ? 'این بخش بسته است'
            : items.length === 0
              ? 'سبد خالی است'
              : isBuy
                ? 'ادامه و ثبت سفارش خرید'
                : 'ادامه و ثبت سفارش فروش'}
        </Link>
      </div>
    </div>
  )
}

/**
 * صفحه سبد خرید و فروش
 * دو قسمت جدا (خرید و فروش) که کنار هم نمایش داده می‌شوند
 */
const Cart: React.FC = () => {
  const { items } = useCart()

  if (items.length === 0) {
    return (
      <div className="container-main py-16">
        <EmptyState
          title="سبد خرید و فروش شما خالی است"
          description="برای خرید یا فروش شمش، آب‌شده، سکه و بار، از صفحه محصولات شروع کنید."
        />
        <div className="text-center">
          <Link to="/products" className="btn btn-primary">مشاهده محصولات</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container-main py-10">
      <h1 className="text-2xl font-black text-gray-900 mb-1">سبد خرید و فروش</h1>
      <p className="text-sm text-gray-500 mb-8">
        دو بخش جدا و کنار هم: سفارش‌های خرید شما از ما و سفارش‌های فروش شما به ما
      </p>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <CartPanel mode="BUY" />
        <CartPanel mode="SELL" />
      </div>

      <div className="mt-8 text-center">
        <Link to="/products" className="btn btn-secondary">ادامه خرید و فروش</Link>
      </div>
    </div>
  )
}

export default Cart
